import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';
import { apiFetch, ApiError } from '../../lib/api';
import { Skeleton } from '../ui/Skeleton';
import { readBillingSnapshot, writeBillingSnapshot, snapshotToSubscriptionData } from '../../lib/billingSnapshot';
import type { SubscriptionData } from '../../lib/billingSnapshot';
import { queryKeys } from '../../lib/queryKeys';
import { trackPurchaseCompleted } from '../../lib/gaEvents';

interface MembershipSectionProps {
  checkoutSuccess?: boolean;
  expectedTier?: string | null;
  expectedInterval?: string | null;
  onCheckoutHandled?: () => void;
}

const TIER_LABELS: Record<string, string> = {
  free: 'Free',
  social: 'Filmple Social',
  pro: 'Filmple Pro',
};

const TIER_COLORS: Record<string, string> = {
  free: 'text-white',
  social: 'text-teal-400',
  pro: 'text-purple-400',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function MembershipSection({ checkoutSuccess, expectedTier, expectedInterval, onCheckoutHandled }: MembershipSectionProps) {
  const { user, session, profile, loading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Initialize sub from billing snapshot for instant flicker-free display
  const [sub, setSub] = useState<SubscriptionData | null>(() => {
    const snap = readBillingSnapshot(user?.id);
    return snap ? snapshotToSubscriptionData(snap) : null;
  });
  const [fetchError, setFetchError] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);

  // Hydrate from snapshot when user becomes available (handles auth race where user is null at mount)
  useEffect(() => {
    if (!user?.id) return;
    if (sub) return; // already hydrated
    const snap = readBillingSnapshot(user.id);
    if (snap) setSub(snapshotToSubscriptionData(snap));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps — intentional: run once when user arrives, not when sub changes

  // Track whether at least one live /subscription fetch has completed (snapshot doesn't count)
  const hasLiveSubRef = useRef(false);

  // Polling refs
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);
  const hasHandledCheckoutRef = useRef(false);
  const hasTrackedPurchaseRef = useRef(false);
  // Keep token ref fresh so polling closures never use a stale JWT
  const tokenRef = useRef(session?.access_token);
  tokenRef.current = session?.access_token;

  const handleConfirmedUpgrade = (data: SubscriptionData) => {
    if (!hasTrackedPurchaseRef.current && (data.subscription_tier === 'social' || data.subscription_tier === 'pro')) {
      hasTrackedPurchaseRef.current = true;
      trackPurchaseCompleted(data.subscription_tier, {
        billing_source: data.billing_source ?? undefined,
        billing_interval: expectedInterval ?? undefined,
      });
    }

    const label = TIER_LABELS[data.subscription_tier] || data.subscription_tier;
    toast.success(`Welcome to ${label}!`);
    const forYouUserId = user?.id ?? profile?.id;
    if (forYouUserId) queryClient.removeQueries({ queryKey: queryKeys.forYou.apiBase(forYouUserId), exact: false });
    refreshProfile();
    onCheckoutHandled?.();
  };

  // Fetch subscription data (uses tokenRef to avoid stale closures)
  const fetchSubscription = async (signal?: AbortSignal): Promise<SubscriptionData | null> => {
    const token = tokenRef.current;
    if (!token) return null;
    try {
      const data = await apiFetch<SubscriptionData>('/subscription', {
        token,
        signal,
      });
      return data;
    } catch {
      return null;
    }
  };

  // Initial fetch
  useEffect(() => {
    if (!session?.access_token) return;
    const controller = new AbortController();

    (async () => {
      const data = await fetchSubscription(controller.signal);
      if (controller.signal.aborted) return;
      if (data) {
        setSub(data);
        hasLiveSubRef.current = true;
        writeBillingSnapshot(user?.id, data, refreshProfile);
        setFetchError(false);
      } else {
        setFetchError(true);
      }
    })();

    return () => controller.abort();
  }, [session?.access_token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture snapshot baseline AFTER user is available (not inline — user may be null at mount)
  const snapshotBaselineRef = useRef<string | null>(null);
  useEffect(() => {
    if (!user?.id) return;
    if (snapshotBaselineRef.current !== null) return; // already captured
    const snap = readBillingSnapshot(user.id);
    snapshotBaselineRef.current = snap?.subscription_tier ?? null;
  }, [user?.id]);

  // Helper: check if subscription data confirms the expected upgrade.
  // Requires at least one live API response — never confirms from snapshot alone.
  const isUpgradeConfirmed = (data: SubscriptionData): boolean => {
    if (!hasLiveSubRef.current) return false;

    const TIER_ORDER: Record<string, number> = { free: 0, social: 1, pro: 2 };
    if (expectedTier) {
      return (TIER_ORDER[data.subscription_tier] ?? 0) >= (TIER_ORDER[expectedTier] ?? 0);
    }
    // No expected_tier: confirm when live tier is paid or differs from baseline
    const baseline = snapshotBaselineRef.current;
    if (data.subscription_tier === 'social' || data.subscription_tier === 'pro') {
      return baseline === null || baseline !== data.subscription_tier;
    }
    return false;
  };

  // Checkout success polling — fires toast only after live /subscription confirms expected tier
  useEffect(() => {
    if (!isActivationFlow || !session?.access_token || hasHandledCheckoutRef.current) return;
    hasHandledCheckoutRef.current = true;
    cancelledRef.current = false;

    // Check if already confirmed (webhook processed during redirect)
    if (sub && isUpgradeConfirmed(sub)) {
      handleConfirmedUpgrade(sub);
      return;
    }

    let attempts = 0;
    const maxAttempts = 15;

    pollingRef.current = setInterval(async () => {
      if (cancelledRef.current) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        return;
      }

      attempts++;
      const data = await fetchSubscription();

      if (cancelledRef.current) return;

      if (data) {
        setSub(data);
        hasLiveSubRef.current = true;
        writeBillingSnapshot(user?.id, data, refreshProfile);

        if (isUpgradeConfirmed(data)) {
          if (pollingRef.current) clearInterval(pollingRef.current);
          handleConfirmedUpgrade(data);
          return;
        }
      }

      if (attempts >= maxAttempts) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        toast.info('Taking longer than expected. Your plan should be active shortly — try refreshing.', { duration: 6000 });
        onCheckoutHandled?.();
      }
    }, 2000);

    return () => {
      cancelledRef.current = true;
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [checkoutSuccess, expectedTier, session?.access_token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manage billing handler
  const handleManageBilling = async () => {
    if (!session?.access_token) return;
    setBillingLoading(true);
    try {
      const data = await apiFetch<{ url: string }>('/billing-portal', {
        token: session.access_token,
        method: 'POST',
      });
      window.location.href = data.url;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'NO_SUBSCRIPTION') {
          toast.error('No active subscription found');
        } else if (err.code === 'BILLING_SOURCE_CONFLICT') {
          toast.info('Your subscription is managed through the mobile app.');
        } else {
          toast.error('Failed to open billing portal. Please try again.');
        }
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      setBillingLoading(false);
    }
  };

  // Loading state
  if (loading || !profile) {
    return (
      <div className="bg-[#1A1F26] rounded-xl p-6">
        <Skeleton className="h-6 w-32 mb-4" />
        <Skeleton className="h-5 w-full mb-3" />
        <Skeleton className="h-5 w-full mb-3" />
        <Skeleton className="h-5 w-2/3" />
      </div>
    );
  }

  // Billing fields derived exclusively from sub (snapshot or live) — profile is never used for billing
  const tier = sub?.subscription_tier || null;
  const resolvedTier = tier || 'free'; // only used where a string is required
  const tierLabel = tier ? (TIER_LABELS[tier] || tier) : null;
  const tierColor = tier ? (TIER_COLORS[tier] || 'text-white') : 'text-white';
  const billingSource = sub?.billing_source || null;
  const entitlementExpiresAt = sub?.entitlement_expires_at || null;
  const messagesUsed = Math.max(0, profile.chat_messages_this_month ?? 0);
  const isPro = resolvedTier === 'pro';

  // Checkout return guard: require live confirmation, not snapshot
  const isActivationFlow = !!checkoutSuccess || !!expectedTier;
  const checkoutActivating = isActivationFlow && (!sub || !isUpgradeConfirmed(sub));

  return (
    <div className="bg-[#1A1F26] rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4">MEMBERSHIP</h2>

      {/* Inline warning when /subscription fetch failed — UI still renders from profile */}
      {fetchError && !sub && (
        <div className="flex items-center justify-between mb-3 px-3 py-2 bg-amber-500/10 rounded-lg">
          <span className="text-amber-400 text-xs">Live subscription details unavailable</span>
          <button
            onClick={async () => {
              setFetchError(false);
              const data = await fetchSubscription();
              if (data) {
                setSub(data);
                hasLiveSubRef.current = true;
                writeBillingSnapshot(user?.id, data, refreshProfile);
              } else {
                setFetchError(true);
              }
            }}
            className="text-xs text-amber-400 hover:text-amber-300 underline ml-2"
          >
            Retry
          </button>
        </div>
      )}

      <div className="space-y-3">
        {/* Plan row — show activating placeholder during checkout return, skeleton when no data yet */}
        {checkoutActivating ? (
          <div className="flex items-center justify-between">
            <span className="text-slate-300">Current Plan</span>
            <span className="text-teal-400 font-semibold animate-pulse">Activating your plan...</span>
          </div>
        ) : sub === null ? (
          <div className="flex items-center justify-between">
            <span className="text-slate-300">Current Plan</span>
            <Skeleton className="h-5 w-28" />
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-slate-300">Current Plan</span>
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${tierColor}`}>{tierLabel}</span>
              {billingSource && billingSource !== 'manual' && (
                <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded">
                  {billingSource === 'stripe'
                    ? 'Web (Stripe)'
                    : billingSource === 'revenuecat'
                      ? 'In-app (iOS/Android)'
                      : billingSource === 'test'
                        ? 'Test'
                        : billingSource}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Renewal / cancel / entitlement status row — wait for sub data to avoid flash */}
        {resolvedTier !== 'free' && (() => {
          const statusLoaded = sub !== null || fetchError;
          if (!statusLoaded) {
            return (
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Status</span>
                <Skeleton className="h-5 w-32" />
              </div>
            );
          }

          // Compute status text + color with full precedence chain
          let statusText: string | null = null;
          let statusColor = 'text-slate-300';

          if (sub?.cancel_at_period_end && sub.current_period_end) {
            statusText = `Cancels ${formatDate(sub.current_period_end)}`;
            statusColor = 'text-amber-400';
          } else if (sub?.status === 'past_due') {
            statusText = 'Payment past due';
            statusColor = 'text-red-400';
          } else if (sub?.status === 'active' && sub.current_period_end) {
            statusText = `Renews ${formatDate(sub.current_period_end)}`;
          } else if (sub?.status === 'trialing' && sub.current_period_end) {
            statusText = `Trial ends ${formatDate(sub.current_period_end)}`;
          } else if (!sub?.current_period_end && entitlementExpiresAt) {
            statusText = `Access until ${formatDate(entitlementExpiresAt)}`;
          }

          if (!statusText) return null;

          return (
            <div className="flex items-center justify-between">
              <span className="text-slate-300">Status</span>
              <span className={statusColor}>{statusText}</span>
            </div>
          );
        })()}

        {/* Chat usage row — skeleton while sub is loading to avoid free-tier flash */}
        <div className="flex items-center justify-between">
          <span className="text-slate-300">Filmply chats this month</span>
          {sub === null ? (
            <Skeleton className="h-5 w-20" />
          ) : (
            <span className="text-white">
              {isPro ? 'Unlimited' : `${messagesUsed}/10`}
            </span>
          )}
        </div>

        {/* Action buttons — hidden during checkout activation or when no data yet */}
        {!checkoutActivating && sub !== null && <div className="pt-4 border-t border-white/[0.06] space-y-2">
          {resolvedTier === 'free' && (
            <button
              onClick={() => navigate('/pricing')}
              className="w-full px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors font-medium"
            >
              Upgrade
            </button>
          )}

          {resolvedTier === 'social' && billingSource === 'stripe' && (
            <>
              <button
                onClick={() => navigate('/pricing')}
                className="w-full px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors font-medium"
              >
                Upgrade to Pro
              </button>
              <button
                onClick={handleManageBilling}
                disabled={billingLoading}
                className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
              >
                {billingLoading ? 'Loading...' : 'Manage Billing'}
              </button>
              <p className="text-xs text-slate-500 text-center mt-2">
                Downgrades and cancellations are managed in Billing and take effect at the end of your current period.
              </p>
            </>
          )}

          {resolvedTier === 'pro' && billingSource === 'stripe' && (
            <>
              <button
                onClick={() => navigate('/pricing')}
                className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium"
              >
                Change Plan
              </button>
              <button
                onClick={handleManageBilling}
                disabled={billingLoading}
                className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50"
              >
                {billingLoading ? 'Loading...' : 'Manage Billing'}
              </button>
              <p className="text-xs text-slate-500 text-center mt-2">
                Downgrades and cancellations are managed in Billing and take effect at the end of your current period.
              </p>
            </>
          )}

          {billingSource === 'revenuecat' && resolvedTier !== 'free' && (
            <p className="text-sm text-slate-400 text-center">
              Managed through mobile app
            </p>
          )}

          {resolvedTier !== 'free' && billingSource !== 'stripe' && billingSource !== 'revenuecat' && (
            <button
              onClick={() => navigate('/pricing')}
              className="text-sm text-slate-400 hover:text-white underline text-center w-full"
            >
              View Plans
            </button>
          )}
        </div>}
      </div>
    </div>
  );
}
